import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  FormControlLabel,
  useTheme,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import { Autocomplete } from "@material-ui/lab";
import SwitchCommon from "../../../../../../components/switches/SwitchCommon";
import { CustomTheme } from "../../../../../../types/customTheme";

interface AvailabilitySectionProps {
  isValidProductAvailability: boolean;
  onAvailabilityToggle: (checked: boolean) => void;
  allYouCanEat: boolean;
  onAllYouCanEatToggle: (checked: boolean) => void;
  isValidKiosk: boolean;
  onKioskToggle: (checked: boolean) => void;
  classes: any;
  // Time and week props
  weekFrom?: number | string;
  setWeekFrom?: (value: number) => void;
  weekTo?: number | string;
  setWeekTo?: (value: number) => void;
  timeFrom?: number | string;
  setTimeFrom?: (value: number) => void;
  timeTo?: number | string;
  setTimeTo?: (value: number) => void;
  onWeekFromChange?: (value: number) => void;
  onWeekToChange?: (value: number) => void;
  onTimeFromChange?: (value: number) => void;
  onTimeToChange?: (value: number) => void;
}

// Helper functions to convert between number and string
const getDayName = (dayNumber: number): string => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return days[dayNumber - 1] || "Monday";
};

const getDayNumber = (dayName: string): number => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return days.indexOf(dayName) + 1 || 1;
};

const getTimeString = (hourNumber: number): string => {
  if (hourNumber === 0) return "12:00 AM";
  if (hourNumber < 12) return `${hourNumber.toString().padStart(2, "0")}:00 AM`;
  if (hourNumber === 12) return "12:00 PM";
  return `${(hourNumber - 12).toString().padStart(2, "0")}:00 PM`;
};

const getTimeNumber = (timeString: string): number => {
  const match = timeString.match(/(\d+):00\s*(AM|PM)/);
  if (!match) return 0;
  let hour = parseInt(match[1]);
  const ampm = match[2];
  if (ampm === "PM" && hour !== 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;
  return hour;
};

const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({
  isValidProductAvailability,
  onAvailabilityToggle,
  allYouCanEat,
  onAllYouCanEatToggle,
  isValidKiosk,
  onKioskToggle,
  classes,
  weekFrom: propWeekFrom,
  setWeekFrom: propSetWeekFrom,
  weekTo: propWeekTo,
  setWeekTo: propSetWeekTo,
  timeFrom: propTimeFrom,
  setTimeFrom: propSetTimeFrom,
  timeTo: propTimeTo,
  setTimeTo: propSetTimeTo,
  onWeekFromChange,
  onWeekToChange,
  onTimeFromChange,
  onTimeToChange,
}) => {
  const theme: CustomTheme = useTheme();

  // Convert props to display values
  const getInitialWeekFrom = () => {
    if (propWeekFrom !== undefined && propWeekFrom !== null) {
      return typeof propWeekFrom === "number"
        ? getDayName(propWeekFrom)
        : getDayName(parseInt(propWeekFrom as string) || 1);
    }
    return "Monday";
  };

  const getInitialWeekTo = () => {
    if (propWeekTo !== undefined && propWeekTo !== null) {
      return typeof propWeekTo === "number"
        ? getDayName(propWeekTo)
        : getDayName(parseInt(propWeekTo as string) || 7);
    }
    return "Sunday";
  };

  const getInitialTimeFrom = () => {
    if (propTimeFrom !== undefined && propTimeFrom !== null) {
      return typeof propTimeFrom === "number"
        ? getTimeString(propTimeFrom)
        : getTimeString(parseInt(propTimeFrom as string) || 0);
    }
    return "10:00 AM";
  };

  const getInitialTimeTo = () => {
    if (propTimeTo !== undefined && propTimeTo !== null) {
      return typeof propTimeTo === "number"
        ? getTimeString(propTimeTo)
        : getTimeString(parseInt(propTimeTo as string) || 23);
    }
    return "11:00 PM";
  };

  // Local state for display values
  const [localWeekFrom, setLocalWeekFrom] = useState<string>(
    getInitialWeekFrom(),
  );
  const [localWeekTo, setLocalWeekTo] = useState<string>(getInitialWeekTo());
  const [localTimeFrom, setLocalTimeFrom] = useState<string>(
    getInitialTimeFrom(),
  );
  const [localTimeTo, setLocalTimeTo] = useState<string>(getInitialTimeTo());

  // Update local state when props change
  useEffect(() => {
    if (propWeekFrom !== undefined && propWeekFrom !== null) {
      const dayName =
        typeof propWeekFrom === "number"
          ? getDayName(propWeekFrom)
          : getDayName(parseInt(propWeekFrom as string) || 1);
      setLocalWeekFrom(dayName);
    }
  }, [propWeekFrom]);

  useEffect(() => {
    if (propWeekTo !== undefined && propWeekTo !== null) {
      const dayName =
        typeof propWeekTo === "number"
          ? getDayName(propWeekTo)
          : getDayName(parseInt(propWeekTo as string) || 7);
      setLocalWeekTo(dayName);
    }
  }, [propWeekTo]);

  useEffect(() => {
    if (propTimeFrom !== undefined && propTimeFrom !== null) {
      const timeStr =
        typeof propTimeFrom === "number"
          ? getTimeString(propTimeFrom)
          : getTimeString(parseInt(propTimeFrom as string) || 0);
      setLocalTimeFrom(timeStr);
    }
  }, [propTimeFrom]);

  useEffect(() => {
    if (propTimeTo !== undefined && propTimeTo !== null) {
      const timeStr =
        typeof propTimeTo === "number"
          ? getTimeString(propTimeTo)
          : getTimeString(parseInt(propTimeTo as string) || 23);
      setLocalTimeTo(timeStr);
    }
  }, [propTimeTo]);

  // Handlers
  const handleWeekFromChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalWeekFrom(newValue);
    const dayNumber = getDayNumber(newValue);
    if (propSetWeekFrom) {
      propSetWeekFrom(dayNumber);
    }
    if (onWeekFromChange) {
      onWeekFromChange(dayNumber);
    }
  };

  const handleWeekToChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalWeekTo(newValue);
    const dayNumber = getDayNumber(newValue);
    if (propSetWeekTo) {
      propSetWeekTo(dayNumber);
    }
    if (onWeekToChange) {
      onWeekToChange(dayNumber);
    }
  };

  const handleTimeFromChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalTimeFrom(newValue);
    const hourNumber = getTimeNumber(newValue);
    if (propSetTimeFrom) {
      propSetTimeFrom(hourNumber);
    }
    if (onTimeFromChange) {
      onTimeFromChange(hourNumber);
    }
  };

  const handleTimeToChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalTimeTo(newValue);
    const hourNumber = getTimeNumber(newValue);
    if (propSetTimeTo) {
      propSetTimeTo(hourNumber);
    }
    if (onTimeToChange) {
      onTimeToChange(hourNumber);
    }
  };

  // Week days options
  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Time options
  const timeOptions = [
    "12:00 AM",
    "01:00 AM",
    "02:00 AM",
    "03:00 AM",
    "04:00 AM",
    "05:00 AM",
    "06:00 AM",
    "07:00 AM",
    "08:00 AM",
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
    "05:00 PM",
    "06:00 PM",
    "07:00 PM",
    "08:00 PM",
    "09:00 PM",
    "10:00 PM",
    "11:00 PM",
  ];

  return (
    <Paper
      elevation={0}
      style={{
        marginBottom: "24px",
        borderRadius: "16px",
        backgroundColor: theme.palette.background.menuProductColor,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Box style={{ padding: "20px 24px" }}>
        {/* Section Header */}
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            style={{
              backgroundColor: "#5e35b1",
              borderRadius: "8px",
              padding: "6px",
              marginRight: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon
              icon="lucide:calendar-clock"
              width="20"
              style={{ color: "white" }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              style={{ fontWeight: "bold" }}
              align="left"
            >
              Availability
            </Typography>
            <Typography variant="caption" align="left">
              Control when this wrapper and its products are available.
            </Typography>
          </Box>
        </Box>

        <Box mt={4}>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Left Column: Status Card */}
            <Grid item xs={12} md={3}>
              <Paper
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: "12px",
                  padding: "16px",
                }}
                elevation={0}
              >
                <Typography
                  variant="body2"
                  style={{ fontWeight: 500, marginBottom: "12px" }}
                  align="left"
                >
                  Product Availability{" "}
                  <span style={{ color: "#f44336" }}>*</span>
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="body2" style={{ marginRight: "12px" }}>
                    Valid
                  </Typography>
                  <FormControlLabel
                    control={<SwitchCommon />}
                    checked={isValidProductAvailability}
                    onChange={(e: any) =>
                      onAvailabilityToggle(e.target.checked)
                    }
                    label=""
                  />
                </Box>
                <Typography
                  variant="caption"
                  style={{ lineHeight: 1.2, display: "block" }}
                  align="left"
                >
                  When disabled, all products under this wrapper will be
                  unavailable.
                </Typography>
              </Paper>
            </Grid>

            {/* Right Column: Time/Day Selectors & Toggles */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                {/* Row 1: Selectors */}
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    style={{ marginBottom: "4px", display: "block" }}
                    align="left"
                  >
                    Week From
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={weekDays}
                    value={localWeekFrom}
                    onChange={handleWeekFromChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    style={{ marginBottom: "4px", display: "block" }}
                    align="left"
                  >
                    Week To
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={weekDays}
                    value={localWeekTo}
                    onChange={handleWeekToChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    style={{ marginBottom: "4px", display: "block" }}
                    align="left"
                  >
                    Time From
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={timeOptions}
                    value={localTimeFrom}
                    onChange={handleTimeFromChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    style={{ marginBottom: "4px", display: "block" }}
                    align="left"
                  >
                    Time To
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={timeOptions}
                    value={localTimeTo}
                    onChange={handleTimeToChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Grid>

                {/* Row 2: Secondary Toggles */}
                <Grid item xs={12} style={{ marginTop: "24px" }}>
                  <Box display="flex" style={{ gap: "100px" }}>
                    <FormControlLabel
                      control={<SwitchCommon />}
                      checked={allYouCanEat}
                      onChange={(e: any) =>
                        onAllYouCanEatToggle(e.target.checked)
                      }
                      label={
                        <Typography
                          variant="body2"
                          style={{ marginLeft: "8px" }}
                        >
                          All You Can Eat
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={<SwitchCommon />}
                      checked={isValidKiosk}
                      onChange={(e: any) => onKioskToggle(e.target.checked)}
                      label={
                        <Typography
                          variant="body2"
                          style={{ marginLeft: "8px" }}
                        >
                          Kiosk
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

export default AvailabilitySection;
